import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { Toaster } from '@/components/ui/toaster';
// Native iOS shell. `platform.js` has zero imports of its own and every module
// under `@/native/**` reaches its Capacitor plugin through loadNative(), so a
// browser never requests those chunks and none of the effects below runs.
import { isNative, loadNative } from '@/lib/platform';
import { installDeepLinkHandler } from '@/native/auth';
import NativeMicPrimerGate from '@/native/media/MicPrimer';
import { installInterruptionBridge } from '@/native/media/interruptionBridge';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { AuthProvider } from '@/contexts/AuthContext';
import { UserProvider } from '@/contexts/UserContext';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import RainBackground from '@/components/RainBackground';
import CanonicalTag from '@/components/CanonicalTag';
// Native-only shell: native chrome CSS, the splash controller that removes the
// cold-start white flash, and the link guard that keeps every tap inside the
// app instead of hopping to Safari. Renders null and registers nothing on web.
import NativeAppShell from '@/native/ui/NativeAppShell';
import ThemeIcons from '@/components/ThemeIcons';
import { useRainSound } from '@/hooks/useRainSound';
// The landing. Serves "/", and it is the only landing there is: the older
// Landing.jsx was deleted with the retired funnels (see archive branch).
import LandingKeiki from '@/pages/LandingKeiki';
// Paid-acquisition entry points stay in the initial chunk: a cold ad click must
// paint the funnel without waiting on a second JS request.
import FbOnboarding from '@/pages/FbOnboarding';
import FbPricing from '@/pages/FbPricing';
import FbSuccess from '@/pages/FbSuccess';
import { captureReferralParams } from '@/lib/referralCapture';
import { LANDING_ROUTE, HELP_ROUTE, HELP_ROUTE_ALIAS } from '@/lib/landingRoute';
import { ProtectedRoute, PublicEntryRoute } from '@/components/routeGuards';
import PendingCodeRedeemer from '@/components/PendingCodeRedeemer';
import GuidedTour, { useGuidedTour } from '@/components/tutorial/GuidedTour';
// Subscription page for managing user subscriptions
import RateLimitModal from '@/components/ui/RateLimitModal';

// Every other page loads on demand. A stale deploy 404s old chunk URLs, so on a
// failed dynamic import reload once to pick up the fresh index.html instead of
// white-screening; if the reload already happened, surface the real error.
const lazyPage = (load) =>
  React.lazy(() =>
    load().catch((err) => {
      let reloaded = false;
      try {
        reloaded = sessionStorage.getItem('ttt_chunk_reloaded') === '1';
        if (!reloaded) sessionStorage.setItem('ttt_chunk_reloaded', '1');
      } catch {
        /* private mode etc. - fall through to a single reload attempt */
      }
      if (!reloaded) {
        window.location.reload();
        return new Promise(() => {});
      }
      throw err;
    })
  );

const Home = lazyPage(() => import('@/pages/Home'));
const HousePage = lazyPage(() => import('@/pages/House/HousePage'));
const Situations = lazyPage(() => import('@/pages/Situations'));
const Settings = lazyPage(() => import('@/pages/Settings'));
const Instructions = lazyPage(() => import('@/pages/Instructions'));
const CustomSituationForm = lazyPage(() => import('@/pages/CustomSituationForm'));
const CustomSituationChat = lazyPage(() => import('@/pages/CustomSituation/Chat'));
const UnknownSituation = lazyPage(() => import('@/pages/UnknownSituation'));
const Awards = lazyPage(() => import('@/pages/Awards'));
const History = lazyPage(() => import('@/pages/History'));
const NonVerbalMode = lazyPage(() => import('@/pages/NonVerbalMode'));
const About = lazyPage(() => import('@/pages/About'));
const AboutUs = lazyPage(() => import('@/pages/AboutUs'));
const Scripts = lazyPage(() => import('@/pages/Scripts'));
const Profile = lazyPage(() => import('@/pages/Profile'));
const OnboardingPreview = lazyPage(() => import('@/components/OnboardingPreview'));
const PushToTalk = lazyPage(() => import('@/pages/PushToTalk'));
const BreathingExercise = lazyPage(() => import('@/pages/BreathingExercise'));
const Live = lazyPage(() => import('@/pages/Live'));
const TimoHome = lazyPage(() => import('@/pages/Timo'));
const ParentDashboard = lazyPage(() => import('@/pages/Parent'));
const Pricing = lazyPage(() => import('@/pages/Pricing'));
const Subscription = lazyPage(() => import('@/pages/Subscription.jsx'));
const Refer = lazyPage(() => import('@/pages/Refer.jsx'));
const Success = lazyPage(() => import('@/pages/Success'));
const Login = lazyPage(() => import('@/pages/Login'));
const AuthCallback = lazyPage(() => import('@/pages/AuthCallback'));
const Download = lazyPage(() => import('@/pages/Download'));
const Privacy = lazyPage(() => import('@/pages/Privacy'));
const Terms = lazyPage(() => import('@/pages/Terms'));
const OpenInBrowser = lazyPage(() => import('@/pages/OpenInBrowser'));
const YcAccess = lazyPage(() => import('@/pages/YcAccess'));
const ScratchOffer = lazyPage(() => import('@/pages/ScratchOffer'));
const FbOnboardingContinue = lazyPage(() => import('@/pages/FbOnboardingContinue'));
const Help = lazyPage(() => import('@/pages/Help'));

// A redirect that carries the query string along. Plain <Navigate to="/x"> drops
// it, and Stripe returns a payer with ?session_id=cs_... appended, which the
// thank-you page needs to verify the payment. Only used on retired paths that a
// live Payment Link could still be pointing at.
const RedirectKeepingQuery = ({ to }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search || ''}`} replace />;
};

// Shown while a lazy route chunk downloads; same spinner the auth guards use.
const RouteLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-400 to-orange-300">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D8795A]"></div>
  </div>
);
import { useRateLimit } from '@/contexts/RateLimitContext';
// Named ConsentBanner, not CookieConsent: ad blockers (Brave shields, uBlock) drop
// any request whose path matches /cookieconsent/, which killed the whole dev bundle.
import ConsentBanner from '@/components/ConsentBanner';
import AiConsentGate from '@/native/compliance/AiConsentGate';
import TimoHeadOpen from '@/assets/headless_Timo.webp';
import TimoHeadWink from '@/assets/Closed_eyes_Timo_Head (2).webp';

// Import background images
import forestBg from '@/assets/forest.webp';
import cityBg from '@/assets/city.webp';
import beachBg from '@/assets/beach.webp';

// Background image map
const BACKGROUND_IMAGES = {
  forest: forestBg,
  city: cityBg,
  beach: beachBg,
};

// ProtectedRoute and PublicEntryRoute live in src/components/routeGuards.jsx,
// so they can be tested on a real hostname (the localhost bypass hides bugs).

// Timo Intro Splash Animation Component
const TimoIntroSplash = ({ onComplete }) => {
  const [phase, setPhase] = useState('grow'); // grow -> wink -> open -> spin -> done
  const [currentImage, setCurrentImage] = useState(TimoHeadOpen);
  
  // Detect iOS for animation fallback
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  useEffect(() => {
    // Shorter animation for iOS or fallback after 3 seconds max
    const duration = isIOS ? 1500 : 3000;
    
    // Phase timing (adjusted for iOS)
    const timers = [
      // Grow phase complete, switch to wink
      setTimeout(() => {
        setPhase('wink');
        setCurrentImage(TimoHeadWink);
      }, isIOS ? 400 : 800),
      // Wink complete, open eyes again
      setTimeout(() => {
        setPhase('open');
        setCurrentImage(TimoHeadOpen);
      }, isIOS ? 700 : 1400),
      // Start spinning and shrinking (disabled on iOS for stability)
      setTimeout(() => {
        if (!isIOS) setPhase('spin');
      }, isIOS ? 900 : 1800),
      // Complete and hide
      setTimeout(() => onComplete(), duration),
      // SAFETY: Force completion after 4 seconds if animations hang
      setTimeout(() => {
        console.warn('[iOS Fix] Forcing intro splash completion due to timeout');
        onComplete();
      }, 4000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete, isIOS]);

  return (
    <div className={`fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden ${phase === 'spin' ? 'animate-bg-collapse' : 'bg-[#1a1a2e]'}`}>
      {/* Diagonal sunset lines */}
      <div 
        className={`absolute w-[200%] h-20 bg-gradient-to-r from-orange-500/70 via-yellow-400/80 to-pink-500/70 transition-all duration-150 ${phase === 'spin' ? 'opacity-0 scale-0' : ''}`}
        style={{ 
          transform: phase === 'spin' ? 'rotate(-35deg) translateY(-150px) scale(0)' : 'rotate(-35deg) translateY(-150px)',
          top: '30%',
          left: '-50%',
          boxShadow: '0 0 40px rgba(255, 180, 50, 0.5)'
        }}
      />
      <div 
        className={`absolute w-[200%] h-20 bg-gradient-to-r from-pink-500/70 via-orange-400/80 to-yellow-500/70 transition-all duration-150 ${phase === 'spin' ? 'opacity-0 scale-0' : ''}`}
        style={{ 
          transform: phase === 'spin' ? 'rotate(-35deg) translateY(150px) scale(0)' : 'rotate(-35deg) translateY(150px)',
          top: '50%',
          left: '-50%',
          boxShadow: '0 0 40px rgba(255, 100, 100, 0.5)'
        }}
      />
      
      {/* Background ball that collapses - skipped on iOS */}
      {!isIOS && (
        <div 
          className={`absolute inset-0 bg-[#1a1a2e] ${phase === 'spin' ? 'animate-bg-ball-collapse' : ''}`}
          style={{ zIndex: -1 }}
        />
      )}
      
      {/* Timo */}
      <div
        className={`
          relative z-10
          ${phase === 'grow' ? 'animate-timo-grow' : ''}
          ${phase === 'wink' ? 'animate-timo-wink-hold' : ''}
          ${phase === 'open' ? 'animate-timo-bounce' : ''}
          ${phase === 'spin' && !isIOS ? 'animate-timo-spin-shrink' : ''}
        `}
      >
        <img 
          src={currentImage} 
          alt="Timo" 
          className="w-72 h-72 object-contain drop-shadow-2xl"
          draggable="false"
        />
      </div>
    </div>
  );
};


function App() {
  const {
    backgroundMode,
    fontSize,
    openDyslexicEnabled,
    onboardingComplete,
    rainBgEnabled,
    themeIconsEnabled,
    reducedMotion,
    lightMode,
  } = useAccessibility();
  const { showModal, hideRateLimitModal } = useRateLimit();
  useRainSound();
  
  // Timo intro splash state
  const [showIntroSplash, setShowIntroSplash] = useState(true);
  
  const handleIntroComplete = useCallback(() => {
    setShowIntroSplash(false);
  }, []);
  
  // 🥚 Secret easter egg
  const [isFlipping, setIsFlipping] = useState(false);
  const [secretBuffer, setSecretBuffer] = useState('');
  const SECRET_CODE = 'bungus';
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only track letter keys, ignore if typing in input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      const key = e.key.toLowerCase();
      if (key.length === 1 && key.match(/[a-z]/)) {
        setSecretBuffer(prev => {
          const newBuffer = (prev + key).slice(-SECRET_CODE.length);
          if (newBuffer === SECRET_CODE) {
            // 🎉 BUNGUS!
            setIsFlipping(true);
            setTimeout(() => setIsFlipping(false), 1000);
            return '';
          }
          return newBuffer;
        });
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  const { showTour, hasChecked, completeTour } = useGuidedTour();
  const location = useLocation();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const hasStartupRedirectRun = useRef(false);
  const backgroundImage = BACKGROUND_IMAGES[backgroundMode] || BACKGROUND_IMAGES.forest;
  const baseFontSize = fontSize === 'medium' ? 16 : fontSize === 'large' ? 18 : 20;

  // Fresh launch behavior: do not restore deep /home/* pages from previous session.
  // If app starts on a nested /home route, reset to the landing screen for
  // logged users, or the public landing for guests.
  useEffect(() => {
    if (hasStartupRedirectRun.current) return;
    hasStartupRedirectRun.current = true;

    const path = location.pathname;
    if (!path.startsWith('/home/') || path === '/home') return;
    // /home/classic is a home screen, not a deep page: a cold open on it should
    // stay there instead of bouncing to the Timo room it is the fallback for.
    if (path === '/home/classic') return;

    const hasLocalUser = Boolean(localStorage.getItem('ttt_user_data'));
    if (hasLocalUser) {
      navigate(LANDING_ROUTE, { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  }, [navigate, location.pathname]);

  // Stripe-return recovery. Stripe appends ?session_id=cs_... to whatever URL a
  // Payment Link redirects to, and that id only exists after a real payment. If
  // it turns up on any path that is not a thank-you page, the redirect landed in
  // the wrong place - carry the payer to the thank-you page instead of leaving
  // them on a landing page wondering whether their card was charged. Both
  // thank-you pages are excluded: /fb/success fires the ads pixel with the
  // stashed purchase value, so yanking an ads payer off it would lose the
  // conversion event.
  const hasStripeRecoveryRun = useRef(false);
  useEffect(() => {
    if (hasStripeRecoveryRun.current) return;
    hasStripeRecoveryRun.current = true;
    if (/^\/(success|fb\/(success|[a-z]{2}\/success))$/.test(location.pathname)) return;
    const sessionId = new URLSearchParams(location.search).get('session_id');
    if (sessionId && sessionId.startsWith('cs_')) {
      navigate(`/success${location.search}`, { replace: true });
    }
  }, [navigate, location.pathname, location.search]);

  // Referral and affiliate capture, app-wide rather than landing-only. A creator
  // link can point at any page (an article, /pricing, /login), and keying on
  // location.search means a client-side nav to a fresh ?ref= is caught too.
  useEffect(() => {
    captureReferralParams();
  }, [location.pathname, location.search]);

  // Native only: receive the OAuth return deep link. Runs once, never on web.
  // A deep link never touches window.location, so detectSessionInUrl and the
  // AuthCallback poll can never see it. This listener is the only thing that
  // completes a native sign in.
  useEffect(() => {
    if (!isNative()) return;
    void installDeepLinkHandler();
  }, []);

  // Native only: ask iOS which languages it can actually hear and speak, so the
  // language gate uses the device's own answer rather than a measured table.
  // Optional; the static tables are already correct about Lithuanian, which is
  // the only answer that changes behaviour.
  useEffect(() => {
    if (!isNative()) return;
    void (async () => {
      const mod = await loadNative(() => import('@/native/speech'));
      await mod?.initNativeSpeech?.();
    })();
  }, []);

  // Native only: recover the audio session after a call or Siri interruption,
  // and let capture code know when iOS has backgrounded (and therefore muted)
  // the microphone. No-op in every browser.
  useEffect(() => {
    if (!isNative()) return;
    void installInterruptionBridge();
  }, []);

  // Scroll to top on every page navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    posthog?.capture('$pageview');
  }, [location.pathname, posthog]);

  useEffect(() => {
    document.documentElement.style.setProperty('--app-font-size', `${baseFontSize}px`);
    return () => {
      document.documentElement.style.removeProperty('--app-font-size');
    };
  }, [baseFontSize]);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    root.classList.toggle('open-dyslexic-font', Boolean(openDyslexicEnabled));
    body.classList.toggle('open-dyslexic-font', Boolean(openDyslexicEnabled));

    return () => {
      root.classList.remove('open-dyslexic-font');
      body.classList.remove('open-dyslexic-font');
    };
  }, [openDyslexicEnabled]);

  // Apply reduce-motion class based on user preference
  useEffect(() => {
    const body = document.body;
    body.classList.toggle('reduce-motion', Boolean(reducedMotion));
    return () => body.classList.remove('reduce-motion');
  }, [reducedMotion]);

  // Apply light-mode class based on user preference
  useEffect(() => {
    const body = document.body;
    body.classList.toggle('light-mode', Boolean(lightMode));
    return () => body.classList.remove('light-mode');
  }, [lightMode]);

  // Shared shell for the themed full-page modes (Situations, NonVerbalMode):
  // themed background, rain, theme icons, toaster, guided tour, rate-limit
  // modal, analytics. `/home/situations` + `/chat` and `/home/nonverbal` +
  // `/show` render the exact same tree, so it lives here once instead of being
  // copy-pasted per route.
  const themedModePage = (page) => (
    <>
      {showIntroSplash && !reducedMotion && (
        <TimoIntroSplash onComplete={handleIntroComplete} />
      )}
      <div
        className={`min-h-screen transition-all duration-150 relative bg-cover bg-center bg-no-repeat ${isFlipping ? 'animate-bungus-flip' : ''}`}
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        <div className="absolute inset-0 bg-black/10 pointer-events-none" />
        <RainBackground isEnabled={rainBgEnabled} />
        <ThemeIcons isEnabled={themeIconsEnabled} themeId={backgroundMode} />
        <div className="relative z-10">
          <main>
            {page}
            <Toaster />
            {onboardingComplete && hasChecked && showTour && (
              <GuidedTour onComplete={completeTour} />
            )}
            <RateLimitModal open={showModal} onClose={hideRateLimitModal} />
          </main>
        </div>
        <Analytics />
        <SpeedInsights />
      </div>
    </>
  );

  return (
    <AuthProvider>
      <UserProvider>
        <CanonicalTag />
        <NativeAppShell />
        {/* Redeems a code typed before sign-in, and posts referral / affiliate
            attribution, once, whichever page is mounted when the session appears. */}
        <PendingCodeRedeemer />
        <React.Suspense fallback={<RouteLoading />}>
        <Routes>
        {/* ==================== PUBLIC ROUTES ==================== */}
        {/* Everyone can access these without login */}
        <Route path="/" element={<PublicEntryRoute><LandingKeiki /></PublicEntryRoute>} />
        {/* The two paths it was built and previewed at, kept so shared links land
            on the same page rather than 404. */}
        <Route path="/landing" element={<Navigate to="/" replace />} />
        <Route path="/landing-keiki" element={<Navigate to="/" replace />} />
        <Route path="/login" element={<PublicEntryRoute><Login /></PublicEntryRoute>} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/download" element={<Download />} />
        <Route path="/open-in-browser" element={<OpenInBrowser />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        {/* Code-gated door for the YC reviewers. Deliberately not behind
            PublicEntryRoute: it has to render even when a session already exists,
            so a second reviewer on the same machine can still get in. */}
        <Route path="/yc" element={<YcAccess />} />
        {/* Facebook-ads acquisition funnel: standalone, public, no auth gate.
            This is where paid traffic lands, so it stays first-class. */}
        <Route path="/fb/onboarding" element={<FbOnboarding />} />
        <Route path="/fb/pricing" element={<FbPricing />} />
        <Route path="/fb/success" element={<FbSuccess />} />
        {/* Localized paid-ad funnels (es, fr). Same components; the :lang segment
            forces the UI language on mount and keeps the whole funnel
            (onboarding -> pricing -> success) in that language. Hard paywall,
            no trial, identical to the English /fb funnel. */}
        <Route path="/fb/:lang/onboarding" element={<FbOnboarding />} />
        <Route path="/fb/:lang/pricing" element={<FbPricing />} />
        <Route path="/fb/:lang/success" element={<FbSuccess />} />
        {/* Deferred deep-personalization phase, run after payment so it never
            blocks a sale. See PROFILE_STEPS in FbOnboarding/steps.js. */}
        <Route path="/fb/onboarding/continue" element={
          <ProtectedRoute>
            <FbOnboardingContinue />
          </ProtectedRoute>
        } />
        {/* The quiz funnel that used to live here was retired: it created accounts
            with a random password nobody could ever log in with. Its code is on
            the archive/all-onboardings-2026-08-09 branch. */}
        <Route path="/start" element={<Navigate to="/login" replace />} />
        {/* Hidden dev-access: hash-gated onboarding preview, no auth/tracking. */}
        <Route path="/preview" element={<OnboardingPreview />} />
        <Route path="/preview/:key" element={<OnboardingPreview />} />
        <Route path="/live" element={
          <ProtectedRoute>
            <Live />
          </ProtectedRoute>
        } />
        
        {/* ==================== PROTECTED ROUTES ==================== */}
        {/* All routes below require authentication */}
        
        {/* THE onboarding. There is exactly one now: the funnel in
            pages/FbOnboarding, served here for organic traffic and at /fb/*
            for paid. The second implementation (components/onboarding) was
            retired; its code is on archive/all-onboardings-2026-08-09.

            Deliberately unguarded, same as /fb/onboarding.
            - ProtectedRoute would send an anonymous visitor to "/", which is
              precisely the cold visitor this funnel exists to convert: it
              creates the account itself at the email step near the end.
            - PublicEntryRoute would be worse than useless here: it redirects a
              logged-in user with unfinished onboarding TO /onboarding, so
              wrapping this route in it is a redirect loop.
            - OnboardingRoute only sent active subscribers to /home, and no
              route in the app sends an active subscriber here (both callers,
              ProtectedRoute's expired-promo branch and PublicEntryRoute, only
              fire when there is no active subscription). It also gates the
              first render on subscription state, which an anonymous visitor
              has no reason to wait behind.
            `/web/onboarding` is an old alias kept so existing links do not 404. */}
        <Route path="/onboarding" element={<FbOnboarding />} />
        <Route path="/web/onboarding" element={<Navigate to="/onboarding" replace />} />
        
        {/* Payment flow. Every page in it is public, on purpose.

            The price reveal sits between onboarding and the paywall. It reads and
            writes only its own localStorage key and charges nothing.

            /pricing used to sit behind ProtectedRoute, which sent anyone without a
            live session to "/". That turned the reveal's Continue button into a
            trip back to the landing page: the visitor who just answered twenty
            questions got dumped at the front door. A paywall has nothing private
            on it, showing the price to a stranger is the entire point of it, and
            it reads the account only to prefill the Stripe email when there is
            one. Guarding it could only ever lose sales. */}
        <Route path="/scratch" element={<ScratchOffer />} />
        <Route path="/pricing" element={<Pricing />} />
        {/* The thank-you page is deliberately unguarded. Safari's tracking
            protection routinely drops the Supabase session across the round trip
            to Stripe, so a guard here bounces the person who just paid back to
            the landing page. The page verifies the Stripe session itself and
            signs the payer back in. */}
        <Route path="/success" element={<Success />} />

        {/* Referral page (logged in, no active subscription required so trial users can share) */}
        <Route path="/refer" element={
          <ProtectedRoute>
            <Refer />
          </ProtectedRoute>
        } />

        {/* THE landing screen: the Timo talk room. Every login, OAuth return
            and paywall redirect lands here through src/lib/landingRoute.js.
            requireSubscription stays ON: the helper picks where, this guard
            still decides who (unpaid goes to /pricing as before).
            skipAuth only unlocks the localhost dev bypass (gated on
            import.meta.env.DEV inside ProtectedRoute), so production still
            requires a real session and an active subscription.
            It carries what /home used to: the intro splash, toaster, rate
            limit modal and analytics, so moving the landing here loses none. */}
        <Route path={LANDING_ROUTE} element={
          <ProtectedRoute requireSubscription={true} skipAuth>
            {showIntroSplash && !reducedMotion && (
              <TimoIntroSplash onComplete={handleIntroComplete} />
            )}
            <React.Suspense fallback={
              <div className="min-h-screen flex items-center justify-center" style={{ background: '#1a1530' }}>
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white/70" />
              </div>
            }>
              <TimoHome />
            </React.Suspense>
            <Toaster />
            <RateLimitModal open={showModal} onClose={hideRateLimitModal} />
            <Analytics />
            <SpeedInsights />
          </ProtectedRoute>
        } />

        {/* How to use: public on purpose, nothing private on it, and a teacher
            can read it before she has an account. /kaip-naudotis is the
            Lithuanian address for the same page. */}
        <Route path={HELP_ROUTE} element={<Help />} />
        <Route path={HELP_ROUTE_ALIAS} element={<Help />} />

        {/* Timo House: swipeable 4-room game home. Staged here on a direct URL
            only, so it is not wired into the home screen while it is tested on
            a real device. Not linked from any nav on purpose. */}
        <Route path="/house" element={
          <ProtectedRoute requireSubscription={true}>
            <React.Suspense fallback={
              <div className="min-h-screen flex items-center justify-center" style={{ background: '#1a1530' }}>
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white/70" />
              </div>
            }>
              <HousePage />
            </React.Suspense>
          </ProtectedRoute>
        } />

        {/* Parent-facing progress dashboard for the Timo kid surface: streak
            calendar, words timeline, stars. Reads localStorage first so it
            paints instantly, then syncs in the background. Kept for bookmarks
            only: the room's "Grown-ups" button now opens settings, so nothing
            in the app links here (see HIDDEN_HOME_ROUTES in landingRoute.js).
            Same for /house above and /home/classic below. */}
        <Route path="/parent" element={
          // skipAuth is the localhost-only dev bypass (gated on
          // import.meta.env.DEV inside ProtectedRoute).
          // Production still requires a real session and an active sub.
          <ProtectedRoute requireSubscription={true} skipAuth>
            <ParentDashboard />
          </ProtectedRoute>
        } />

        {/* Clean top-level entry points for the two modes the Timo room links
            out to. They render exactly the same element as the older
            /home/situations and /home/nonverbal paths, which stay live because
            plenty of code still navigates to them. skipAuth is the same
            localhost-only dev bypass /timo and /parent use (no effect outside
            import.meta.env.DEV), so the room dock works while developing. */}
        <Route path="/chat" element={
          <ProtectedRoute skipAuth>
            {themedModePage(<Situations />)}
          </ProtectedRoute>
        } />
        <Route path="/show" element={
          <ProtectedRoute skipAuth>
            {themedModePage(<NonVerbalMode />)}
          </ProtectedRoute>
        } />

        {/* App routes (at top level with /home/ prefix) */}
        {/* /home was the Timo room and rendered the same screen as the landing
            route. It now forwards there (query kept, so ?demo=1 still works),
            so a bookmark or an old back button reaches the one landing screen
            instead of a second copy of it. The /home/* routes below are
            separate and unchanged. */}
        <Route path="/home" element={<RedirectKeepingQuery to={LANDING_ROUTE} />} />
        {/* The previous /home screen (greeting plus mode cards), kept whole so
            it can be made the home screen again by swapping the two paths. */}
        <Route path="/home/classic" element={
          <ProtectedRoute requireSubscription={true}>
            {showIntroSplash && !reducedMotion && (
              <TimoIntroSplash onComplete={handleIntroComplete} />
            )}
            <div
              className={`min-h-screen transition-all duration-150 relative bg-cover bg-center bg-no-repeat ${isFlipping ? 'animate-bungus-flip' : ''}`}
              style={{ backgroundImage: `url(${backgroundImage})` }}
            >
              <div className="absolute inset-0 bg-black/10 pointer-events-none" />
              <RainBackground isEnabled={rainBgEnabled} />
              <ThemeIcons isEnabled={themeIconsEnabled} themeId={backgroundMode} />
              <div className="relative z-10">
                <main>
                  <Home />
                  <Toaster />
                  {onboardingComplete && hasChecked && showTour && (
                    <GuidedTour onComplete={completeTour} />
                  )}
                  <RateLimitModal open={showModal} onClose={hideRateLimitModal} />
                </main>
              </div>
              <Analytics />
              <SpeedInsights />
            </div>
          </ProtectedRoute>
        } />
        <Route path="/home/breathing" element={
          <ProtectedRoute>
            <BreathingExercise />
          </ProtectedRoute>
        } />
        <Route path="/home/situations" element={
          <ProtectedRoute>
            {themedModePage(<Situations />)}
          </ProtectedRoute>
        } />
        <Route path="/home/instructions/:situationId" element={
          <ProtectedRoute>
            <Instructions />
          </ProtectedRoute>
        } />
        <Route path="/home/practice" element={
          <ProtectedRoute>
            <UnknownSituation />
          </ProtectedRoute>
        } />
        <Route path="/home/practice/chat" element={
          <ProtectedRoute>
            <CustomSituationChat />
          </ProtectedRoute>
        } />
        <Route path="/home/custom-situation/new" element={
          <ProtectedRoute>
            <CustomSituationForm />
          </ProtectedRoute>
        } />
        <Route path="/home/custom-situation/edit/:situationId" element={
          <ProtectedRoute>
            <CustomSituationForm />
          </ProtectedRoute>
        } />
        <Route path="/home/settings" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/home/awards" element={
          <ProtectedRoute>
            <Awards />
          </ProtectedRoute>
        } />
        <Route path="/home/history" element={
          <ProtectedRoute>
            <History />
          </ProtectedRoute>
        } />
        <Route path="/home/about" element={
          <ProtectedRoute>
            <About />
          </ProtectedRoute>
        } />
        <Route path="/home/about-us" element={
          <ProtectedRoute>
            <AboutUs />
          </ProtectedRoute>
        } />
        <Route path="/home/scripts" element={
          <ProtectedRoute>
            <Scripts />
          </ProtectedRoute>
        } />
        <Route path="/home/subscription" element={
          <ProtectedRoute>
            <Subscription />
          </ProtectedRoute>
        } />
        <Route path="/home/profile" element={
          <ProtectedRoute>
            {showIntroSplash && !reducedMotion && (
              <TimoIntroSplash onComplete={handleIntroComplete} />
            )}
            <div 
              className={`min-h-screen transition-all duration-150 relative bg-cover bg-center bg-no-repeat ${isFlipping ? 'animate-bungus-flip' : ''}`}
              style={{ backgroundImage: `url(${backgroundImage})` }}
            >
              <div className="absolute inset-0 bg-black/10 pointer-events-none" />
              <RainBackground isEnabled={rainBgEnabled} />
              <ThemeIcons isEnabled={themeIconsEnabled} themeId={backgroundMode} />
              <div className="relative z-10">
                <main>
                  <Profile />
                  <Toaster />
                  {onboardingComplete && hasChecked && showTour && (
                    <GuidedTour onComplete={completeTour} />
                  )}
                  <RateLimitModal open={showModal} onClose={hideRateLimitModal} />
                </main>
              </div>
              <Analytics />
              <SpeedInsights />
            </div>
          </ProtectedRoute>
        } />
        <Route path="/home/nonverbal" element={
          <ProtectedRoute>
            {themedModePage(<NonVerbalMode />)}
          </ProtectedRoute>
        } />
        <Route path="/home/push-to-talk" element={
          <ProtectedRoute>
            <PushToTalk />
          </ProtectedRoute>
        } />

        {/* Catch all - redirect to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </React.Suspense>
      <NativeMicPrimerGate />
      <ConsentBanner />
      <AiConsentGate />
      {/* ServiceNoticeModal removed: it announced "chat is back up and running"
          from a past outage and confused new users (esp. FB funnel signups who
          never saw the outage). Re-add if a fresh service notice is needed. */}
      </UserProvider>
    </AuthProvider>
  );
}

export default App;